import React from 'react';
import { Alert, Button, Descriptions, Popconfirm, Space, Typography } from 'antd';
import { ClockCircleOutlined, BankOutlined } from '@ant-design/icons';
import { useTranslation } from '../../shared/i18n';
import { formatMoney } from '../../shared/utils/money';

const { Text, Link } = Typography;

const NETWORK_LABEL = { aba: 'ACH / Wire (US)', swift: 'SWIFT (international)', iban: 'IBAN', sort_code: 'UK sort code', zengin: 'Zengin', spei: 'SPEI' };

/**
 * A Stripe payment that is in flight for this bill (the bill stays unpaid until Stripe confirms it):
 *  - processing:        ACH debit etc. clearing (up to a few business days)
 *  - awaiting_transfer: bank transfer — show our virtual account + reference
 *  - partially_funded:  part of the transfer arrived; show the remaining amount
 *  - requires_action:   e.g. bank account microdeposit verification
 */
const StripePendingPayment = ({ pending, onPayAnotherWay, replacing }) => {
    const { t } = useTranslation();
    if (!pending) return null;
    const na = pending.next_action || {};

    if (pending.state === 'processing') {
        return (
            <Alert type="info" showIcon icon={<ClockCircleOutlined />}
                message={t('Payment processing')}
                description={t('Bank payments can take up to 4 business days. The bill is marked paid automatically once the funds arrive.')} />
        );
    }

    if (pending.state === 'requires_action' && na.type === 'verify_with_microdeposits') {
        return (
            <Alert type="warning" showIcon
                message={t('Verify your bank account')}
                description={na.hosted_verification_url
                    ? <Link href={na.hosted_verification_url} target="_blank" rel="noopener noreferrer">{t('Enter the microdeposit amounts')}</Link>
                    : t('Check your bank statement for two small deposits and enter them to finish the payment.')} />
        );
    }

    if (na.type !== 'display_bank_transfer_instructions') return null;
    const partial = pending.state === 'partially_funded';
    const remaining = na.amount_remaining != null ? formatMoney(na.amount_remaining, na.currency || pending.currency, { original: true }) : null;

    return (
        <Alert
            type={partial ? 'warning' : 'info'}
            showIcon
            icon={<BankOutlined />}
            message={partial ? t('Part of your transfer arrived. Send the remaining {{amount}}', { amount: remaining })
                : t('Waiting for your bank transfer of {{amount}}', { amount: remaining })}
            description={(
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">{t('Include the reference so the payment is matched to this bill.')}</Text>
                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label={t('Reference')}><Text copyable strong>{na.reference}</Text></Descriptions.Item>
                        <Descriptions.Item label={t('Amount')}>{remaining}</Descriptions.Item>
                    </Descriptions>
                    {(na.financial_addresses || []).map((fa, i) => (
                        <Descriptions key={i} size="small" column={1} bordered title={NETWORK_LABEL[fa.type] || fa.type}>
                            {fa.bank_name && <Descriptions.Item label={t('Bank')}>{fa.bank_name}</Descriptions.Item>}
                            {fa.account_holder_name && <Descriptions.Item label={t('Account holder')}>{fa.account_holder_name}</Descriptions.Item>}
                            {fa.account_number && <Descriptions.Item label={t('Account number')}><Text copyable>{fa.account_number}</Text></Descriptions.Item>}
                            {fa.routing_number && <Descriptions.Item label={t('Routing number')}><Text copyable>{fa.routing_number}</Text></Descriptions.Item>}
                            {fa.swift_code && <Descriptions.Item label="SWIFT / BIC"><Text copyable>{fa.swift_code}</Text></Descriptions.Item>}
                            {fa.iban && <Descriptions.Item label="IBAN"><Text copyable>{fa.iban}</Text></Descriptions.Item>}
                        </Descriptions>
                    ))}
                    <Space wrap>
                        {na.hosted_instructions_url && (
                            <Link href={na.hosted_instructions_url} target="_blank" rel="noopener noreferrer">{t('Printable instructions')}</Link>
                        )}
                        {!partial && onPayAnotherWay && (
                            <Popconfirm
                                title={t("Already sent the transfer? Don't switch — it will be matched automatically.")}
                                okText={t('Pay another way')}
                                cancelText={t('Back')}
                                onConfirm={onPayAnotherWay}
                            >
                                <Button size="small" loading={replacing}>{t('Pay another way')}</Button>
                            </Popconfirm>
                        )}
                    </Space>
                </Space>
            )}
        />
    );
};

export default StripePendingPayment;
